import * as styles from "./TaskDialog.css";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  type JSX,
  type ReactNode,
  type RefObject,
  useMemo,
  useState,
} from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import {
  Field,
  FieldContent,
  FieldContext,
  FieldLabel,
  makeFieldContext,
} from "@frontend/components/atoms/Field";
import { Fieldset, FieldsetLegend } from "@frontend/components/atoms/Fieldset";
import { Icon } from "@frontend/components/atoms/Icon";
import type { IconName } from "@frontend/components/atoms/Icon";
import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import { DateRecurrencePicker } from "@frontend/components/organisms/DateRecurrencePicker";
import { useField } from "@frontend/hooks/form";
import { useDiscardGuard } from "@frontend/hooks/useDiscardGuard";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import type { InferFieldsFromSchema, ItemAtom } from "@frontend/lib/form";
import { DiscardChangesDialog } from "@frontend/shared/discard-changes-dialog";
import { ReferenceField } from "@frontend/shared/reference-field";
import { Trans, useLingui } from "@lingui/react/macro";

import { useTaskTypes } from "../hooks/useTaskTypes";
import type { TaskValues, taskSchema } from "../schemas/job";
import {
  cancelTaskEditAtom,
  editedTaskAtom,
  jobForm,
  referencesAtom,
} from "../stores/editor";
import { TaskHours } from "./TaskHours";

export type TaskFields = InferFieldsFromSchema<typeof taskSchema>;

const DIALOG_WIDTH = 720;

const gridFields = makeFieldContext({
  orientation: "vertical",
  grouped: false,
});

function Group({
  icon,
  legend,
  children,
}: {
  icon?: IconName;
  legend?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <Fieldset variant="section">
      {legend ? <FieldsetLegend icon={icon}>{legend}</FieldsetLegend> : null}

      <FieldContext.Provider value={gridFields}>
        <div className={styles.grid}>{children}</div>
      </FieldContext.Provider>
    </Fieldset>
  );
}

export type TaskDialogProps = {
  index: number;
  fields: TaskFields;
  itemAtom: ItemAtom;
  created: boolean;
  onRemove: () => void;
  originRef?: RefObject<DOMRect | null>;
};

export function TaskDialog({
  index,
  fields,
  itemAtom,
  created,
  onRemove,
  originRef,
}: TaskDialogProps): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const references = useAtomValue(referencesAtom);
  const errors = useAtomValue(jobForm.state.errors);

  const close = useSetAtom(editedTaskAtom);
  const cancelEdit = useSetAtom(cancelTaskEditAtom);
  const touch = useSetAtom(jobForm.actions.touch);

  const serviceGroupId = useAtomValue(fields.serviceGroupId.atom);
  const deletable = useAtomValue(fields.deletable.atom);

  const typeId = useField(fields.typeId);
  const rule = useField(fields.recurrence.fields.rule);

  const [exceptions, setExceptions] = useAtom(
    fields.recurrence.fields.exceptions.valueAtom,
  );

  const taskTypes = useTaskTypes(serviceGroupId ?? null, references.taskTypes);

  const current = useAtomValue(itemAtom) as TaskValues;

  const [snapshot] = useState(current);

  const dirty = JSON.stringify(current) !== JSON.stringify(snapshot);

  const guard = useDiscardGuard({
    dirty,
    onClose: () => cancelEdit(snapshot),
  });

  const failing = useMemo(() => {
    const prefix = `tasks.${index}.`;

    return Object.keys(errors).filter((path) => path.startsWith(prefix));
  }, [errors, index]);

  const confirm = () => {
    if (0 < failing.length) {
      for (const path of failing) {
        touch(path);
      }

      return;
    }

    close(null);
  };

  const position = index + 1;

  const chosenType =
    taskTypes.find(({ id }) => id === typeId.value)?.label ?? null;

  return (
    <Dialog open onOpenChange={guard.onOpenChange} originRef={originRef}>
      <DialogContent size={DIALOG_WIDTH}>
        <DialogHeader>
          <DialogTitle className={styles.title}>
            {created ? t`New task` : t`Task ${position}`}

            {chosenType ? (
              <span className={styles.subtitle}>{chosenType}</span>
            ) : null}
          </DialogTitle>

          <DialogClose />
        </DialogHeader>

        <ScrollArea className={styles.scrollArea}>
          <ScrollAreaViewport fade>
            <ScrollAreaContent className={styles.body} style={{ minWidth: 0 }}>
              <Group icon="Tags" legend={t`Work`}>
                <ReferenceField
                  className={styles.span.half}
                  label={t`Service group`}
                  placeholder={t`Choose a group`}
                  items={references.serviceGroups}
                  field={fields.serviceGroupId}
                  onValueChange={() => typeId.setValue(null)}
                />

                <ReferenceField
                  className={styles.span.half}
                  label={t`Task type`}
                  placeholder={t`Choose a type`}
                  items={taskTypes}
                  field={fields.typeId}
                />

                <ReferenceField
                  className={styles.span.full}
                  label={t`Work category`}
                  placeholder={t`Choose a category`}
                  items={references.categories}
                  field={fields.workCategoryId}
                />
              </Group>

              <Fieldset variant="section">
                <FieldsetLegend icon="Clock">
                  <Trans>Hours</Trans>
                </FieldsetLegend>

                <TaskHours field={fields.hours} />
              </Fieldset>

              <Group>
                <Field className={styles.span.full}>
                  <FieldLabel>{t`Repeats`}</FieldLabel>

                  <FieldContent>
                    <DateRecurrencePicker
                      locale={locale}
                      value={rule.value ?? ""}
                      exceptions={exceptions}
                      onValueChange={(next) => rule.setValue(next)}
                      onExceptionsChange={(next) =>
                        setExceptions(
                          next.map((exception) => ({
                            id: exception.id ?? null,
                            type: exception.type,
                            start: exception.start ?? null,
                            stop: exception.stop ?? null,
                            nbWeeks: exception.nbWeeks ?? null,
                          })),
                        )
                      }
                    />
                  </FieldContent>
                </Field>

                <ReferenceField
                  className={styles.span.half}
                  label={t`Day tolerance`}
                  items={references.dayLags}
                  field={fields.dayLagId}
                />

                <ReferenceField
                  className={styles.span.half}
                  label={t`Time tolerance`}
                  items={references.timeLags}
                  field={fields.timeLagId}
                />
              </Group>
            </ScrollAreaContent>
          </ScrollAreaViewport>

          <ScrollAreaScrollbar expandOnHover>
            <ScrollAreaThumb />
          </ScrollAreaScrollbar>
        </ScrollArea>

        <DialogFooter>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  className={styles.remove}
                  variant="ghost"
                  disabled={false === deletable}
                  aria-label={t`Remove this task`}
                  onClick={onRemove}
                >
                  <Icon name="Trash2" size="sm" />
                </Button>
              }
            />

            <TooltipContent>
              {false === deletable
                ? t`This task carries done or validated hours`
                : t`Remove this task`}
            </TooltipContent>
          </Tooltip>

          <Button variant="neutral" onClick={guard.requestClose}>
            <Trans>Cancel</Trans>
          </Button>

          <Button variant="primary" onClick={confirm}>
            <Trans>Apply</Trans>
          </Button>
        </DialogFooter>

        <DiscardChangesDialog
          open={guard.confirming}
          onKeep={guard.keep}
          onDiscard={guard.discard}
        />
      </DialogContent>
    </Dialog>
  );
}
